/**
 * Article Templates - Plantillas de contenido predefinidas para el blog
 * Fase 5 del Editor de Blog Mejorado
 */

import React, { useState } from 'react';
import { 
  FileText, 
  BarChart2, 
  HelpCircle, 
  List, 
  Newspaper,
  BookOpen,
  Lightbulb,
  Users,
  X,
  Check,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

// Template definitions
export const ARTICLE_TEMPLATES = {
  guia: {
    id: 'guia',
    name: 'Guía Completa',
    description: 'Tutorial paso a paso sobre un tema específico',
    icon: BookOpen,
    color: 'text-blue-400 bg-blue-500/10',
    suggestedCategory: 'Guías',
    structure: {
      title: 'Guía Completa: [Tema] - Todo lo que Necesitas Saber',
      excerpt: 'Descubre todo sobre [tema] en esta guía completa. Aprende paso a paso cómo [objetivo principal].',
      meta_description: 'Guía completa sobre [tema]. Aprende [beneficio principal] con consejos prácticos y ejemplos reales.',
      meta_keywords: '[tema principal], guía, tutorial, cómo, paso a paso, Chile',
      content: `<p class="lead">Si estás buscando información completa sobre <strong>[tema]</strong>, has llegado al lugar correcto. En esta guía te explicaremos todo lo que necesitas saber para [objetivo principal].</p>

<h2>¿Qué es [Tema] y Por Qué es Importante?</h2>
<p>[Explicación introductoria del tema y su relevancia. Incluye estadísticas o datos de Chile si es posible.]</p>

<h2>Paso 1: [Primer Paso Principal]</h2>
<p>[Descripción detallada del primer paso.]</p>
<ul>
<li><strong>Punto clave 1:</strong> [Detalle]</li>
<li><strong>Punto clave 2:</strong> [Detalle]</li>
<li><strong>Punto clave 3:</strong> [Detalle]</li>
</ul>

<h2>Paso 2: [Segundo Paso Principal]</h2>
<p>[Descripción detallada del segundo paso.]</p>

<h3>Consideraciones Importantes</h3>
<p>[Detalles adicionales o advertencias relevantes.]</p>

<h2>Paso 3: [Tercer Paso Principal]</h2>
<p>[Descripción detallada del tercer paso.]</p>

<h2>Errores Comunes que Debes Evitar</h2>
<ul>
<li><strong>Error 1:</strong> [Descripción y cómo evitarlo]</li>
<li><strong>Error 2:</strong> [Descripción y cómo evitarlo]</li>
<li><strong>Error 3:</strong> [Descripción y cómo evitarlo]</li>
</ul>

<h2>Preguntas Frecuentes</h2>

<h3>¿[Pregunta frecuente 1]?</h3>
<p>[Respuesta detallada]</p>

<h3>¿[Pregunta frecuente 2]?</h3>
<p>[Respuesta detallada]</p>

<h3>¿[Pregunta frecuente 3]?</h3>
<p>[Respuesta detallada]</p>

<h2>Conclusión</h2>
<p>[Resumen de los puntos principales y motivación final.]</p>

<p>Si necesitas ayuda profesional con [tema], te invitamos a <a href="/contacto">contactarnos</a> o explorar nuestro <a href="/centros-de-rehabilitacion-drogas-chile">directorio de centros especializados</a>.</p>`
    }
  },
  comparativa: {
    id: 'comparativa',
    name: 'Artículo Comparativo',
    description: 'Comparación detallada entre opciones o alternativas',
    icon: BarChart2,
    color: 'text-purple-400 bg-purple-500/10',
    suggestedCategory: 'Guías',
    structure: {
      title: '[Opción A] vs [Opción B]: Comparación Completa [Año]',
      excerpt: 'Comparamos [Opción A] y [Opción B] para ayudarte a elegir la mejor alternativa según tus necesidades.',
      meta_description: 'Comparación detallada entre [Opción A] y [Opción B]. Descubre cuál es mejor según precio, efectividad y más.',
      meta_keywords: '[opción A], [opción B], comparación, diferencias, cuál es mejor, Chile',
      content: `<p class="lead">¿No sabes si elegir <strong>[Opción A]</strong> o <strong>[Opción B]</strong>? En este artículo comparamos ambas alternativas para ayudarte a tomar la mejor decisión.</p>

<h2>Resumen de la Comparación</h2>

<table class="w-full border-collapse my-6">
<thead>
<tr class="bg-slate-800">
<th class="border border-slate-600 p-3 text-left">Característica</th>
<th class="border border-slate-600 p-3 text-left">[Opción A]</th>
<th class="border border-slate-600 p-3 text-left">[Opción B]</th>
</tr>
</thead>
<tbody>
<tr>
<td class="border border-slate-700 p-3"><strong>Precio</strong></td>
<td class="border border-slate-700 p-3">[Precio A]</td>
<td class="border border-slate-700 p-3">[Precio B]</td>
</tr>
<tr>
<td class="border border-slate-700 p-3"><strong>Duración</strong></td>
<td class="border border-slate-700 p-3">[Duración A]</td>
<td class="border border-slate-700 p-3">[Duración B]</td>
</tr>
<tr>
<td class="border border-slate-700 p-3"><strong>Efectividad</strong></td>
<td class="border border-slate-700 p-3">[Efectividad A]</td>
<td class="border border-slate-700 p-3">[Efectividad B]</td>
</tr>
<tr>
<td class="border border-slate-700 p-3"><strong>Mejor para</strong></td>
<td class="border border-slate-700 p-3">[Perfil ideal A]</td>
<td class="border border-slate-700 p-3">[Perfil ideal B]</td>
</tr>
</tbody>
</table>

<h2>¿Qué es [Opción A]?</h2>
<p>[Descripción detallada de la primera opción.]</p>

<h3>Ventajas de [Opción A]</h3>
<ul>
<li><strong>Ventaja 1:</strong> [Descripción]</li>
<li><strong>Ventaja 2:</strong> [Descripción]</li>
<li><strong>Ventaja 3:</strong> [Descripción]</li>
</ul>

<h3>Desventajas de [Opción A]</h3>
<ul>
<li><strong>Desventaja 1:</strong> [Descripción]</li>
<li><strong>Desventaja 2:</strong> [Descripción]</li>
</ul>

<h2>¿Qué es [Opción B]?</h2>
<p>[Descripción detallada de la segunda opción.]</p>

<h3>Ventajas de [Opción B]</h3>
<ul>
<li><strong>Ventaja 1:</strong> [Descripción]</li>
<li><strong>Ventaja 2:</strong> [Descripción]</li>
<li><strong>Ventaja 3:</strong> [Descripción]</li>
</ul>

<h3>Desventajas de [Opción B]</h3>
<ul>
<li><strong>Desventaja 1:</strong> [Descripción]</li>
<li><strong>Desventaja 2:</strong> [Descripción]</li>
</ul>

<h2>¿Cuál Elegir? Nuestra Recomendación</h2>
<p><strong>Elige [Opción A] si:</strong> [Criterios para elegir A]</p>
<p><strong>Elige [Opción B] si:</strong> [Criterios para elegir B]</p>

<h2>Preguntas Frecuentes</h2>

<h3>¿[Pregunta comparativa 1]?</h3>
<p>[Respuesta]</p>

<h3>¿[Pregunta comparativa 2]?</h3>
<p>[Respuesta]</p>

<h2>Conclusión</h2>
<p>[Resumen final y recomendación.]</p>

<p>¿Necesitas ayuda para decidir? <a href="/contacto">Contáctanos</a> y te asesoraremos según tu situación particular.</p>`
    }
  },
  faq: {
    id: 'faq',
    name: 'Artículo FAQ',
    description: 'Preguntas frecuentes sobre un tema específico',
    icon: HelpCircle,
    color: 'text-amber-400 bg-amber-500/10',
    suggestedCategory: 'General',
    structure: {
      title: '[Tema]: Respuestas a las Preguntas más Frecuentes',
      excerpt: 'Resolvemos todas tus dudas sobre [tema]. Encuentra respuestas claras a las preguntas más comunes.',
      meta_description: 'Preguntas frecuentes sobre [tema]. Respuestas claras y completas a las dudas más comunes.',
      meta_keywords: '[tema], preguntas frecuentes, FAQ, dudas, respuestas, Chile',
      content: `<p class="lead">Si tienes dudas sobre <strong>[tema]</strong>, aquí encontrarás respuestas claras y completas a las preguntas más frecuentes que recibimos.</p>

<h2>Preguntas Generales sobre [Tema]</h2>

<h3>¿Qué es [tema] exactamente?</h3>
<p>[Respuesta detallada y clara. Usa ejemplos cuando sea posible.]</p>

<h3>¿Por qué es importante conocer sobre [tema]?</h3>
<p>[Explica la relevancia y el impacto.]</p>

<h3>¿Cuáles son los principales síntomas/señales de [tema]?</h3>
<p>[Lista de síntomas o señales importantes:]</p>
<ul>
<li>[Síntoma/señal 1]</li>
<li>[Síntoma/señal 2]</li>
<li>[Síntoma/señal 3]</li>
<li>[Síntoma/señal 4]</li>
</ul>

<h2>Preguntas sobre Tratamiento/Solución</h2>

<h3>¿[Tema] tiene tratamiento/solución?</h3>
<p>[Respuesta sobre opciones de tratamiento o solución.]</p>

<h3>¿Cuánto tiempo toma [proceso relacionado]?</h3>
<p>[Respuesta con plazos realistas.]</p>

<h3>¿Cuánto cuesta [tratamiento/servicio relacionado] en Chile?</h3>
<p>[Información sobre costos con rangos de precios.]</p>
<p>Para una estimación personalizada, puedes usar nuestra <a href="/herramientas/calculadora-costos">calculadora de costos</a>.</p>

<h2>Preguntas sobre Ayuda y Recursos</h2>

<h3>¿Dónde puedo encontrar ayuda profesional?</h3>
<p>En Chile existen múltiples opciones para recibir ayuda profesional. Te recomendamos explorar nuestro <a href="/centros-de-rehabilitacion-drogas-chile">directorio de centros especializados</a> o buscar un <a href="/profesionales">profesional cerca de ti</a>.</p>

<h3>¿Cómo puedo ayudar a un familiar/amigo con [tema]?</h3>
<p>[Consejos prácticos para familiares y amigos.]</p>

<h3>¿Qué hago si necesito ayuda urgente?</h3>
<p>[Información sobre recursos de emergencia y contactos importantes.]</p>

<h2>Más Información</h2>

<h3>¿Dónde puedo obtener más información sobre [tema]?</h3>
<p>Te invitamos a explorar otros artículos de nuestro <a href="/blog">blog</a> donde abordamos este tema en profundidad.</p>

<h3>¿Tienen algún test o evaluación gratuita?</h3>
<p>Sí, ofrecemos tests de autoevaluación gratuitos. Puedes probar nuestro <a href="/herramientas/test-alcoholismo">test de alcoholismo</a> o el <a href="/herramientas/test-dependencia">test de dependencia a drogas</a>.</p>

<h2>¿Tienes más preguntas?</h2>
<p>Si tu pregunta no está aquí, no dudes en <a href="/contacto">contactarnos</a>. Estamos para ayudarte.</p>`
    }
  },
  listicle: {
    id: 'listicle',
    name: 'Artículo de Lista',
    description: 'Lista numerada con consejos, tips o recursos',
    icon: List,
    color: 'text-emerald-400 bg-emerald-500/10',
    suggestedCategory: 'Guías',
    structure: {
      title: '[Número] [Consejos/Tips/Señales/Formas] para [Objetivo]',
      excerpt: 'Descubre los mejores [consejos/tips] para [objetivo]. Una guía práctica con [número] recomendaciones efectivas.',
      meta_description: '[Número] [consejos/tips] efectivos para [objetivo]. Guía práctica con recomendaciones de expertos.',
      meta_keywords: '[tema], consejos, tips, [objetivo], guía, Chile',
      content: `<p class="lead">¿Buscas [consejos/tips] efectivos para <strong>[objetivo]</strong>? Aquí te compartimos [número] recomendaciones prácticas que realmente funcionan.</p>

<h2>1. [Primer Consejo/Tip]</h2>
<p>[Descripción detallada del primer punto. Explica el por qué y el cómo.]</p>
<blockquote>
<strong>Tip práctico:</strong> [Consejo específico de implementación]
</blockquote>

<h2>2. [Segundo Consejo/Tip]</h2>
<p>[Descripción detallada del segundo punto.]</p>

<h2>3. [Tercer Consejo/Tip]</h2>
<p>[Descripción detallada del tercer punto.]</p>

<h2>4. [Cuarto Consejo/Tip]</h2>
<p>[Descripción detallada del cuarto punto.]</p>

<h2>5. [Quinto Consejo/Tip]</h2>
<p>[Descripción detallada del quinto punto.]</p>

<h2>6. [Sexto Consejo/Tip]</h2>
<p>[Descripción detallada del sexto punto.]</p>

<h2>7. [Séptimo Consejo/Tip]</h2>
<p>[Descripción detallada del séptimo punto.]</p>

<h2>Resumen: Puntos Clave</h2>
<ul>
<li><strong>[Consejo 1]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 2]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 3]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 4]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 5]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 6]:</strong> [Resumen breve]</li>
<li><strong>[Consejo 7]:</strong> [Resumen breve]</li>
</ul>

<h2>¿Necesitas Ayuda Adicional?</h2>
<p>Si estos consejos no son suficientes para tu situación, considera buscar <a href="/profesionales">ayuda profesional</a>. A veces necesitamos apoyo experto para lograr nuestros objetivos.</p>

<p>También puedes explorar nuestros <a href="/herramientas">tests de autoevaluación</a> para entender mejor tu situación actual.</p>`
    }
  },
  informativo: {
    id: 'informativo',
    name: 'Artículo Informativo',
    description: 'Información educativa sobre un tema de salud',
    icon: Lightbulb,
    color: 'text-cyan-400 bg-cyan-500/10',
    suggestedCategory: 'Adicciones',
    structure: {
      title: '[Tema]: Qué Es, Síntomas, Causas y Tratamiento',
      excerpt: 'Todo lo que necesitas saber sobre [tema]: qué es, cómo identificarlo, sus causas y las opciones de tratamiento disponibles.',
      meta_description: 'Información completa sobre [tema]: síntomas, causas, efectos y opciones de tratamiento en Chile.',
      meta_keywords: '[tema], síntomas, causas, tratamiento, información, salud, Chile',
      content: `<p class="lead"><strong>[Tema]</strong> es una condición que afecta a [estadística o descripción del alcance]. En este artículo te explicamos todo lo que necesitas saber para comprenderla y buscar ayuda.</p>

<h2>¿Qué es [Tema]?</h2>
<p>[Definición clara y completa del tema. Incluye información médica o científica de forma accesible.]</p>

<h2>Síntomas y Señales de Alerta</h2>
<p>Es importante reconocer los síntomas tempranamente. Los más comunes incluyen:</p>

<h3>Síntomas Físicos</h3>
<ul>
<li>[Síntoma físico 1]</li>
<li>[Síntoma físico 2]</li>
<li>[Síntoma físico 3]</li>
</ul>

<h3>Síntomas Psicológicos</h3>
<ul>
<li>[Síntoma psicológico 1]</li>
<li>[Síntoma psicológico 2]</li>
<li>[Síntoma psicológico 3]</li>
</ul>

<h3>Señales en el Comportamiento</h3>
<ul>
<li>[Señal conductual 1]</li>
<li>[Señal conductual 2]</li>
<li>[Señal conductual 3]</li>
</ul>

<h2>Causas y Factores de Riesgo</h2>
<p>[Introducción sobre las causas.]</p>

<h3>Factores Genéticos</h3>
<p>[Explicación de factores genéticos o hereditarios.]</p>

<h3>Factores Ambientales</h3>
<p>[Explicación de factores del entorno.]</p>

<h3>Factores Psicológicos</h3>
<p>[Explicación de factores emocionales o mentales.]</p>

<h2>Consecuencias y Efectos</h2>
<p>[Descripción de las consecuencias a corto y largo plazo.]</p>

<h2>Opciones de Tratamiento</h2>
<p>Afortunadamente, existen tratamientos efectivos. Las principales opciones incluyen:</p>

<h3>Tratamiento Ambulatorio</h3>
<p>[Descripción del tratamiento ambulatorio y para quién es recomendado.]</p>

<h3>Tratamiento Residencial</h3>
<p>[Descripción del tratamiento residencial y para quién es recomendado.]</p>

<h3>Terapias Complementarias</h3>
<p>[Descripción de otras opciones terapéuticas.]</p>

<p>Puedes comparar opciones de tratamiento usando nuestra <a href="/herramientas/calculadora-costos">calculadora de costos</a>.</p>

<h2>¿Cómo Buscar Ayuda?</h2>
<p>El primer paso es reconocer que se necesita ayuda. Te recomendamos:</p>
<ol>
<li>Hacer un <a href="/herramientas/test-dependencia">test de autoevaluación</a></li>
<li>Buscar un <a href="/profesionales">profesional especializado</a></li>
<li>Explorar <a href="/centros-de-rehabilitacion-drogas-chile">centros de tratamiento</a> en tu zona</li>
</ol>

<h2>Conclusión</h2>
<p>[Mensaje de esperanza y resumen de los puntos principales.]</p>

<p>¿Tienes dudas? <a href="/contacto">Contáctanos</a> y te orientaremos de forma gratuita y confidencial.</p>`
    }
  },
  testimonio: {
    id: 'testimonio',
    name: 'Historia de Recuperación',
    description: 'Testimonio o caso de éxito inspirador',
    icon: Users,
    color: 'text-rose-400 bg-rose-500/10',
    suggestedCategory: 'Recuperación',
    structure: {
      title: 'Historia de [Nombre]: Cómo Superé [Adicción/Problema]',
      excerpt: 'Conoce la historia de [Nombre], quien logró superar [problema] y hoy vive una vida plena. Una historia de esperanza y recuperación.',
      meta_description: 'Historia real de recuperación: [Nombre] comparte cómo superó [problema] y las lecciones que aprendió en el proceso.',
      meta_keywords: 'historia de recuperación, testimonio, superación, adicción, rehabilitación, Chile',
      content: `<p class="lead"><em>"[Cita inspiradora del protagonista de la historia]"</em></p>

<p>Esta es la historia de <strong>[Nombre]</strong>, quien a los [edad] años enfrentó uno de los mayores desafíos de su vida: [breve descripción del problema].</p>

<h2>El Comienzo: Cómo Empezó Todo</h2>
<p>[Descripción de cómo comenzó el problema. Contextualiza la situación de forma empática.]</p>

<h3>Las Primeras Señales</h3>
<p>[Describe las primeras señales que aparecieron.]</p>

<h2>El Punto de Quiebre</h2>
<p>[Describe el momento en que la persona decidió buscar ayuda o tocó fondo.]</p>

<blockquote>
"[Cita del protagonista sobre este momento crucial]"
</blockquote>

<h2>El Camino hacia la Recuperación</h2>
<p>[Describe el proceso de tratamiento y recuperación.]</p>

<h3>Los Primeros Pasos</h3>
<p>[Qué acciones tomó al principio.]</p>

<h3>El Tratamiento</h3>
<p>[Describe el tipo de tratamiento que siguió y su experiencia.]</p>

<h3>Los Desafíos</h3>
<p>[Describe los obstáculos que enfrentó durante la recuperación.]</p>

<h2>La Vida Hoy</h2>
<p>[Describe cómo es la vida del protagonista actualmente.]</p>

<h2>Lecciones Aprendidas</h2>
<p>[Nombre] comparte las principales lecciones de su experiencia:</p>
<ul>
<li><strong>[Lección 1]:</strong> [Detalle]</li>
<li><strong>[Lección 2]:</strong> [Detalle]</li>
<li><strong>[Lección 3]:</strong> [Detalle]</li>
</ul>

<h2>Un Mensaje de Esperanza</h2>
<blockquote>
"[Mensaje final inspirador del protagonista para quienes están pasando por algo similar]"
</blockquote>

<h2>¿Necesitas Ayuda?</h2>
<p>Si te identificas con esta historia, recuerda que <strong>la recuperación es posible</strong>. Miles de personas en Chile han logrado superar situaciones similares.</p>

<p>Da el primer paso:</p>
<ul>
<li>Realiza un <a href="/herramientas/test-dependencia">test de autoevaluación gratuito</a></li>
<li>Explora <a href="/centros-de-rehabilitacion-drogas-chile">centros de rehabilitación</a> cerca de ti</li>
<li><a href="/contacto">Contáctanos</a> para orientación confidencial</li>
</ul>

<p><em>Nota: Algunos detalles de esta historia han sido modificados para proteger la privacidad del protagonista.</em></p>`
    }
  }
};

// Template Card Component
function TemplateCard({ template, selected, onSelect }) {
  const Icon = template.icon;
  
  return (
    <button
      onClick={() => onSelect(template.id)}
      className={`w-full p-4 rounded-lg border text-left transition-all ${
        selected 
          ? 'border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/50' 
          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${template.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-200">{template.name}</h4>
            {selected && <Check className="w-4 h-4 text-emerald-400" />}
          </div>
          <p className="text-sm text-slate-400 mt-1">{template.description}</p>
          <span className="inline-block mt-2 px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
            {template.suggestedCategory}
          </span>
        </div>
      </div>
    </button>
  );
}

// Template Selector Modal
export function TemplateSelector({ open, onOpenChange, onSelectTemplate }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleApply = () => {
    if (selectedTemplate) {
      const template = ARTICLE_TEMPLATES[selectedTemplate];
      onSelectTemplate(template);
      onOpenChange(false);
      setSelectedTemplate(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-400" />
            Seleccionar Plantilla de Artículo
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-slate-400 mb-4">
            Elige una plantilla para comenzar con una estructura predefinida. Podrás personalizar todo el contenido después.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.values(ARTICLE_TEMPLATES).map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                selected={selectedTemplate === template.id}
                onSelect={setSelectedTemplate}
              />
            ))}
          </div>
          
          {selectedTemplate && (
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-2">Vista previa de la estructura:</h4>
              <div className="text-sm text-slate-400">
                <p><strong>Título sugerido:</strong> {ARTICLE_TEMPLATES[selectedTemplate].structure.title}</p>
                <p className="mt-1"><strong>Categoría:</strong> {ARTICLE_TEMPLATES[selectedTemplate].suggestedCategory}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleApply} 
            disabled={!selectedTemplate}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <FileText className="w-4 h-4 mr-2" />
            Usar Plantilla
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Template Quick Button (for toolbar)
export function TemplateQuickButton({ onSelectTemplate }) {
  const [open, setOpen] = useState(false);
  
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-2"
      >
        <FileText className="w-4 h-4" />
        Usar Plantilla
      </Button>
      <TemplateSelector 
        open={open} 
        onOpenChange={setOpen}
        onSelectTemplate={onSelectTemplate}
      />
    </>
  );
}

export default TemplateSelector;
